// =============================================================================
// index.js - NUCLEAR OPTION: Bulletproof MSAL Initialization
// =============================================================================
// This implementation ensures MSAL is initialized AFTER all configuration is
// fully loaded, with multiple validation layers and fallback mechanisms
// =============================================================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './styles/tokens.css';
import './styles/colors.css';
import './styles/design-system.css';
import './styles/typography.css';
import './index.css';
import App from './App';
import { MsalProvider } from '@azure/msal-react';
// Platform-public: no @azure/msal-browser; useMsal/MsalProvider come from utils/msalPublicStub.js (craco alias)

// Import authLogger and logger (local)
import { authLogger } from './utils/authLogger';
import { logger } from './utils/logger';

// Import configuration loader to initialize localhost detection and configuration
import './utils/configLoader';
import { disableConsoleInProd } from './utils/disableConsoleInProd';
// Theme vars from config/env/.platform_fe_public_config
import { THEME_CSS_VAR_NAMES } from 'feSharedIndex';
import { displayStartupLogoSync } from './utils/logoDisplay';

// No console output in prod (lower envs only)
disableConsoleInProd();
if (typeof window !== 'undefined') {
  setTimeout(disableConsoleInProd, 100);
}

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.__THEME_CSS_VAR_NAMES = THEME_CSS_VAR_NAMES;
}

// Display logo IMMEDIATELY - wrapped in try-catch to never break initialization
try {
  displayStartupLogoSync();
} catch (e) {
  // Silently fail - logo display should never break app
  console.debug('Startup logo skipped:', e);
}
// Unconditional bootstrap log so we know this bundle has the latest init (v2). If you see the logo but NOT this, the bundle is old.
if (typeof window !== 'undefined') {
  console.warn('[platform-public] v2 bootstrap (sync, after logo)');
}

// ============================================================================
// IMMEDIATE URL CAPTURE (before any async operations)
// ============================================================================
// Capture the URL immediately when script loads to preserve auth callback state
const IMMEDIATE_URL_CAPTURE = {
  href: window.location.href,
  pathname: window.location.pathname,
  hash: window.location.hash,
  search: window.location.search,
  timestamp: Date.now()
};
// Keep production logs minimal; deep auth diagnostics are behind DEBUG.
// eslint-disable-next-line no-unused-vars
const __URL_CAPTURE__ = IMMEDIATE_URL_CAPTURE;

// ============================================================================
// ROOT MANAGEMENT
// ============================================================================

// Global root instance to prevent multiple createRoot() calls
let globalRoot = null;
let rootCreationAttempted = false;

// Get or create root instance (singleton pattern)
function getOrCreateRoot() {
  if (globalRoot) {
    return globalRoot;
  }
  
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    logger.error('INIT', 'Root element not found');
    return null;
  }
  
  if (rootCreationAttempted) {
    logger.warn('INIT', 'Root creation already attempted, cannot create again');
    if (rootElement._reactRootContainer) {
      globalRoot = rootElement._reactRootContainer;
      return globalRoot;
    }
    return null;
  }
  
  try {
    rootCreationAttempted = true;
    globalRoot = ReactDOM.createRoot(rootElement);
    return globalRoot;
  } catch (e) {
    if (e.message && e.message.includes('already been passed to createRoot')) {
      logger.warn('INIT', 'Root already exists (created elsewhere), cannot create new one');
      if (rootElement._reactRootContainer) {
        globalRoot = rootElement._reactRootContainer;
        return globalRoot;
      }
      return null;
    }
    logger.error('INIT', 'Failed to create React root', { error: e.message });
    rootElement.innerHTML = '<div style="padding: 20px; text-align: center;"><h2>React Initialization Failed</h2><p>Please refresh the page.</p></div>';
    return null;
  }
}

// ============================================================================
// CONFIGURATION LOADER WITH TIMEOUT PROTECTION
// ============================================================================

/**
 * Wait for window._env_ to be available with timeout
 * @param {number} maxWaitTime - Maximum time to wait in milliseconds
 * @returns {Promise<object|null>} Configuration object or null if timeout
 */
async function waitForEnvironmentConfig(maxWaitTime = 5000) {
  const startTime = Date.now();
  
  return new Promise((resolve) => {
    const checkConfig = () => {
      if (window._env_) {
        authLogger.debug('[Init] window._env_ loaded successfully');
        authLogger.debug('[Init] ENTRA_AUTHORITY_URL:', window._env_.ENTRA_AUTHORITY_URL);
        resolve(window._env_);
        return;
      }
      
      const elapsedTime = Date.now() - startTime;
      
      if (elapsedTime > maxWaitTime) {
        logger.warn('INIT', `[Init] Timeout waiting for window._env_ after ${maxWaitTime}ms`);
        resolve(null);
        return;
      }
      
      // Check again after 100ms
      setTimeout(checkConfig, 100);
    };
    
    checkConfig();
  });
}

/**
 * Verify MSAL configuration before initialization
 * PLATFORM-PUBLIC: This function is not used (MSAL is disabled for public site)
 * @param {PublicClientApplication} msalInstance - MSAL instance to verify
 * @returns {boolean} True if configuration is valid
 */
// PLATFORM-PUBLIC: MSAL not used; kept for API compatibility if needed later.
// eslint-disable-next-line no-unused-vars
function verifyMsalConfiguration(msalInstance) {
  return true;
}

// ============================================================================
// LOADING UI
// ============================================================================

// Track current loading step for progress indication
let currentLoadingStep = 0;
const loadingSteps = [
  { message: 'Loading configuration...', progress: 10 },
  { message: 'Initializing authentication...', progress: 30 },
  { message: 'Verifying security settings...', progress: 50 },
  { message: 'Preparing your workspace...', progress: 70 },
  { message: 'Almost ready...', progress: 90 },
  { message: 'Welcome!', progress: 100 }
];

function updateLoadingStep(stepIndex, customMessage = null) {
  currentLoadingStep = Math.min(stepIndex, loadingSteps.length - 1);
  // Re-render loading UI with updated step
  const root = getOrCreateRoot();
  if (root) {
    renderLoadingUI(root, customMessage);
  }
}

function renderLoadingUI(root, customMessage = null) {
  // Add enhanced loading styles if not already added
  if (!document.getElementById('loading-spinner-styles')) {
    const style = document.createElement('style');
    style.id = 'loading-spinner-styles';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes shimmer {
        0% { background-position: -1000px 0; }
        100% { background-position: 1000px 0; }
      }
      .loading-spinner {
        animation: spin 1s linear infinite;
      }
      .loading-pulse {
        animation: pulse 2s ease-in-out infinite;
      }
      .loading-fade-in {
        animation: fadeIn 0.5s ease-out;
      }
      .loading-shimmer {
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.1) 0%,
          rgba(255, 255, 255, 0.2) 50%,
          rgba(255, 255, 255, 0.1) 100%
        );
        background-size: 1000px 100%;
        animation: shimmer 2s infinite;
      }
    `;
    document.head.appendChild(style);
  }

  const step = loadingSteps[currentLoadingStep] || loadingSteps[0];
  const displayMessage = customMessage || step.message;
  const progress = step.progress;

  root.render(
    React.createElement('div', {
      style: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #022d54 0%, #011831 50%, #001122 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        overflow: 'hidden'
      }
    },
      // Animated background circles
      React.createElement('div', {
        style: {
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(74, 144, 226, 0.1) 0%, transparent 70%)',
          animation: 'pulse 4s ease-in-out infinite',
          pointerEvents: 'none'
        }
      }),
      React.createElement('div', {
        style: {
          position: 'absolute',
          bottom: '-30%',
          right: '-30%',
          width: '60%',
          height: '60%',
          background: 'radial-gradient(circle, rgba(74, 144, 226, 0.08) 0%, transparent 70%)',
          animation: 'pulse 6s ease-in-out infinite',
          animationDelay: '1s',
          pointerEvents: 'none'
        }
      }),
      // Main content container
      React.createElement('div', {
        className: 'loading-fade-in',
        style: {
          textAlign: 'center',
          color: '#ffffff',
          maxWidth: '480px',
          padding: '48px',
          position: 'relative',
          zIndex: 1
        }
      },
        // Logo/Brand area (optional - can add logo here)
        React.createElement('div', {
          style: {
            marginBottom: '40px'
          }
        },
          React.createElement('div', {
            className: 'loading-spinner',
            style: {
              width: '80px',
              height: '80px',
              border: '4px solid rgba(255, 255, 255, 0.15)',
              borderTop: '4px solid #4a90e2',
              borderRight: '4px solid #4a90e2',
              borderRadius: '50%',
              margin: '0 auto 24px',
              boxShadow: '0 0 20px rgba(74, 144, 226, 0.3)'
            }
          })
        ),
        // Title
        React.createElement('h1', {
          style: {
            fontSize: '32px',
            fontWeight: 700,
            margin: '0 0 12px 0',
            color: '#ffffff',
            letterSpacing: '-0.5px',
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
          }
        }, 'B-Esper'),
        // Status message
        React.createElement('p', {
          className: 'loading-pulse',
          style: {
            fontSize: '18px',
            color: 'rgba(255, 255, 255, 0.9)',
            margin: '0 0 32px 0',
            lineHeight: '1.6',
            fontWeight: 500,
            minHeight: '28px'
          }
        }, displayMessage),
        // Progress bar container
        React.createElement('div', {
          style: {
            width: '100%',
            maxWidth: '320px',
            margin: '0 auto',
            height: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '2px',
            overflow: 'hidden',
            position: 'relative'
          }
        },
          React.createElement('div', {
            className: 'loading-shimmer',
            style: {
              width: `${progress}%`,
              height: '100%',
              backgroundColor: '#4a90e2',
              borderRadius: '2px',
              transition: 'width 0.5s ease-out',
              boxShadow: '0 0 10px rgba(74, 144, 226, 0.5)'
            }
          })
        ),
        // Progress percentage (optional, subtle) - only show if not complete
        ...(progress < 100 ? [React.createElement('p', {
          key: 'progress-percent',
          style: {
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.6)',
            margin: '16px 0 0 0',
            fontWeight: 400
          }
        }, `${progress}%`)] : [])
      )
    )
  );
}

function renderErrorUI(root, error) {
  root.render(
    React.createElement('div', {
      style: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#f8d7da',
        color: '#721c24',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        padding: '20px'
      }
    },
      React.createElement('div', {
        style: {
          maxWidth: '600px',
          padding: '40px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }
      },
        React.createElement('h1', {
          style: {
            fontSize: '28px',
            fontWeight: 600,
            margin: '0 0 16px 0',
            color: '#721c24'
          }
        }, 'Application Initialization Error'),
        React.createElement('p', {
          style: {
            fontSize: '16px',
            color: '#721c24',
            margin: '0 0 24px 0',
            lineHeight: '1.5'
          }
        }, 'Failed to initialize the application. This is typically caused by incorrect MSAL configuration, specifically the Entra External ID authority URL.'),
        React.createElement('p', {
          style: {
            fontSize: '14px',
            color: '#721c24',
            margin: '0 0 24px 0',
            fontWeight: 600
          }
        }, 'Check the browser console for detailed error messages.'),
        React.createElement('pre', {
          style: {
            backgroundColor: '#fff3cd',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
            color: '#856404',
            margin: '0 0 24px 0',
            wordBreak: 'break-word'
          }
        }, error.message || 'Unknown error'),
        React.createElement('h3', {
          style: {
            fontSize: '18px',
            fontWeight: 600,
            margin: '0 0 12px 0',
            color: '#721c24'
          }
        }, 'Troubleshooting Steps:'),
        React.createElement('ol', {
          style: {
            fontSize: '14px',
            color: '#721c24',
            margin: '0 0 24px 0',
            paddingLeft: '20px',
            lineHeight: '1.8'
          }
        },
          React.createElement('li', null, 'Open DevTools Console (F12 or Cmd+Shift+I)'),
          React.createElement('li', null, 'Check for [MSAL Config] error messages'),
          React.createElement('li', null, 'Verify window._env_.ENTRA_AUTHORITY_URL in console'),
          React.createElement('li', null, 'Verify authority contains .onmicrosoft.com domain name, NOT tenant ID'),
          React.createElement('li', null, 'Hard refresh browser (Ctrl+Shift+R or Cmd+Shift+R)'),
          React.createElement('li', null, 'Clear browser storage (LocalStorage, SessionStorage, Cookies)')
        ),
        React.createElement('button', {
          onClick: () => window.location.reload(),
          style: {
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 600,
            color: '#ffffff',
            backgroundColor: '#721c24',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }
        }, 'Refresh Page')
      )
    )
  );
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize MSAL and render React app
 */
async function initializeApp() {
  authLogger.debug('[Init] Starting application initialization...');
  
  // =========================================================================
  // STEP 0.5: IMMEDIATELY check for auth callback (before any async operations)
  // =========================================================================
  // CRITICAL: Check URL immediately to preserve MSAL request state
  // Use the captured URL from script load to avoid any URL manipulation
  const capturedHash = IMMEDIATE_URL_CAPTURE.hash || '';
  const capturedSearch = IMMEDIATE_URL_CAPTURE.search || '';
  const currentHash = window.location.hash || '';
  const currentSearch = window.location.search || '';
  
  // Use captured URL first (most reliable), fallback to current URL
  const hash = capturedHash || currentHash;
  const search = capturedSearch || currentSearch;
  
  const hasAuthCode = hash.includes('code=') || search.includes('code=');
  const hasIdToken = hash.includes('id_token=') || search.includes('id_token=');
  const hasState = hash.includes('state=') || search.includes('state=');
  const isAuthCallback = hasAuthCode || (hasIdToken && hasState);
  
  authLogger.debug('[Init] Step 0: Checking for auth callback in URL...', {
    capturedUrl: IMMEDIATE_URL_CAPTURE,
    currentPathname: window.location.pathname,
    capturedHash: capturedHash.length > 0,
    capturedSearch: capturedSearch.length > 0,
    currentHash: currentHash.length > 0,
    currentSearch: currentSearch.length > 0,
    hasAuthCode,
    hasIdToken,
    hasState,
    isAuthCallback,
    hashPreview: hash.substring(0, 200) + (hash.length > 200 ? '...' : ''),
    searchPreview: search.substring(0, 200) + (search.length > 200 ? '...' : '')
  });
  
  
  // Get root element
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    logger.error('INIT', 'Root element not found');
    return;
  }
  
  // Get or create root
  const root = getOrCreateRoot();
  if (!root) {
    return;
  }
  
  // =========================================================================
  // OPTIMIZATION: Check if config is already loaded or loads very quickly
  // =========================================================================
  // Give config a small window to load before showing loading screen
  // This prevents the flash on refresh when config is already cached
  let showLoadingScreen = true;
  
  // Quick check: if config is already loaded, skip loading screen
  if (window._env_) {
    showLoadingScreen = false;
    authLogger.debug('[Init] Config already loaded, skipping loading screen');
  } else {
    // Wait for config to load with a short timeout (200ms max)
    // This gives the script time to execute if it's already in the DOM
    const configLoadPromise = new Promise((resolve) => {
      let checkCount = 0;
      const maxChecks = 20; // 20 * 10ms = 200ms max
      
      const checkConfig = () => {
        checkCount++;
        if (window._env_) {
          resolve(true);
        } else if (checkCount >= maxChecks) {
          resolve(false);
        } else {
          setTimeout(checkConfig, 10); // Check every 10ms
        }
      };
      
      checkConfig();
    });
    
    const configLoaded = await configLoadPromise;
    
    if (configLoaded) {
      showLoadingScreen = false;
      authLogger.debug('[Init] Config loaded quickly, skipping loading screen');
    } else {
      // Config not loaded yet, show loading screen
      authLogger.debug('[Init] Config not loaded yet, showing loading screen');
      renderLoadingUI(root);
      updateLoadingStep(0, 'Loading configuration...');
    }
  }
  
  // =========================================================================
  // STEP 1: Wait for environment configuration to load
  // =========================================================================
  
  authLogger.debug('[Init] Step 1: Waiting for environment configuration...');
  const envConfig = await waitForEnvironmentConfig(showLoadingScreen ? 5000 : 100);
  
  if (!envConfig) {
    logger.warn('INIT', 'Environment configuration not available');
    // Show loading screen if config failed to load
    if (!showLoadingScreen) {
      showLoadingScreen = true;
      renderLoadingUI(root);
    }
  } else {
    authLogger.debug('[Init] Environment configuration loaded:', {
      authority: envConfig.ENTRA_AUTHORITY_URL,
      clientId: envConfig.ENTRA_CUSTOMER_CLIENT_ID?.substring(0, 8) + '...',
    });
    
    // =========================================================================
    // STEP 1.5: Pre-warm REMOVED
    // =========================================================================
    // REMOVED: Warmup fetch was causing browser to queue all requests for 15 seconds
    // The API responds in 100ms, so warmup is not needed
    // If warmup is needed in the future, it should be done server-side or via a different mechanism
  }
  
  if (showLoadingScreen) {
    updateLoadingStep(1, 'Initializing authentication...');
  }
  
  // =========================================================================
  // STEP 2: Skip MSAL cache clearing for platform-public (no MSAL used)
  // =========================================================================
  
  // PLATFORM-PUBLIC: Skip all MSAL cache operations (public site, no auth)
  logger.debug('INIT', '[Init] Platform-public - skipping MSAL cache operations');
  
  // Skip the entire MSAL cache clearing section (platform-public: no MSAL)

  if (false && isAuthCallback) { // Disabled for platform-public
    authLogger.debug('[Init] Step 2: Skipping cache clear - auth callback detected in URL');
    authLogger.debug('[Init] Preserving MSAL request state for redirect processing');
    authLogger.debug('[Init] Full hash:', hash);
    authLogger.debug('[Init] Full search:', search);
    
    // =========================================================================
    // DIAGNOSTIC: Extract and analyze state from URL vs sessionStorage
    // =========================================================================
    let stateFromUrl = null;
    let interactionIdFromUrl = null;
    
    try {
      const hashParams = new URLSearchParams(hash.substring(1));
      const searchParams = new URLSearchParams(search);
      stateFromUrl = hashParams.get('state') || searchParams.get('state');
      
      if (stateFromUrl) {
        // Decode base64 state parameter to get interaction ID
        try {
          const decodedState = JSON.parse(atob(stateFromUrl));
          interactionIdFromUrl = decodedState.id;
          authLogger.debug('[Init] State from URL decoded:', {
            raw: stateFromUrl.substring(0, 100) + (stateFromUrl.length > 100 ? '...' : ''),
            decoded: decodedState,
            interactionId: interactionIdFromUrl
          });
        } catch (e) {
          logger.warn('INIT', '[Init] Could not decode state parameter', { error: e.message });
          logger.warn('INIT', '[Init] Raw state value:', stateFromUrl.substring(0, 200));
        }
      }
    } catch (e) {
      logger.warn('INIT', '[Init] Error extracting state from URL', { error: e.message });
    }
    
    // BEST PRACTICE: Don't manually manipulate MSAL state
    // MSAL will automatically read its state from localStorage/cookies when handleRedirectPromise() is called
    // We just need to ensure we don't clear the cache on callback page
  } else {
    // CRITICAL FIX: Check for active interaction BEFORE clearing anything
    // This prevents state loss during OAuth redirect callback
    authLogger.debug('[Init] Step 2: Checking for active interactions before any cleanup...');
    
    // CRITICAL: Also check for multiple accounts - this can cause state mismatch issues
    let accountCount = 0;
    try {
      // Try to get account count from MSAL if instance is available
      // This is a diagnostic check to identify potential issues
      const accountsKey = 'msal.account.keys';
      const accountKeysJson = localStorage.getItem(accountsKey);
      if (accountKeysJson) {
        try {
          const accountKeys = JSON.parse(accountKeysJson);
          accountCount = accountKeys.length;
          if (accountCount > 1) {
            logger.warn('INIT', '[Init] Multiple accounts detected in cache', {
              accountCount,
              accountKeys: accountKeys.map(k => k.substring(0, 60) + '...')
            });
            logger.warn('INIT', '[Init] Multiple accounts can cause state mismatch issues during redirects');
            logger.warn('INIT', '[Init] The app will attempt to select the correct account during token acquisition');
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    } catch (e) {
      // Ignore errors during account count check
    }
    
    let hasActiveInteraction = false;
    try {
      const interactionStatus = localStorage.getItem('msal.interaction.status');
      const interactionStart = localStorage.getItem('msal.interaction.start');
      
      // Check if there's an active interaction (redirect, popup, etc.)
      if (interactionStatus && interactionStatus !== 'None' && interactionStatus !== 'null') {
        if (interactionStart) {
          const interactionAge = Date.now() - parseInt(interactionStart, 10);
          // If interaction is less than 10 minutes old, consider it active
          // (OAuth redirects typically complete within seconds, but we allow buffer)
          if (interactionAge < 10 * 60 * 1000) {
            hasActiveInteraction = true;
            authLogger.debug('[Init] Active interaction detected, preserving all MSAL state', {
              status: interactionStatus,
              age: `${Math.round(interactionAge / 1000)}s`,
              accountCount: accountCount > 0 ? accountCount : 'unknown'
            });
            
            // If multiple accounts exist and we have an active interaction, log a warning
            if (accountCount > 1) {
              logger.warn('INIT', '[Init] CRITICAL: Multiple accounts detected during active interaction', {
                accountCount,
                interactionStatus,
                warning: 'This may cause state mismatch - the app will attempt to select the correct account'
              });
            }
          } else {
            logger.warn('INIT', '[Init] Found stuck interaction (older than 10 minutes), will clear after verification');
          }
        } else {
          // If status exists but no start time, it might be a fresh interaction
          hasActiveInteraction = true;
          authLogger.debug('[Init] Interaction status found without start time, preserving state to be safe', {
            accountCount: accountCount > 0 ? accountCount : 'unknown'
          });
        }
      }
    } catch (e) {
      logger.warn('INIT', 'Error checking interaction status', { error: e.message });
      // On error, err on the side of caution - preserve state
      hasActiveInteraction = true;
    }
    
    // CRITICAL: Only clear old sessionStorage data if there's NO active interaction
    // This prevents state loss during OAuth redirect callback
    if (!hasActiveInteraction) {
      authLogger.debug('[Init] Step 2.0: Clearing old sessionStorage MSAL data (we now use localStorage)...');
      try {
        const sessionMsalKeys = Object.keys(sessionStorage).filter(k => k.includes('msal') || k.includes('interaction'));
        if (sessionMsalKeys.length > 0) {
          sessionMsalKeys.forEach(key => {
            sessionStorage.removeItem(key);
            authLogger.debug(`[Init]   Removed old sessionStorage key: ${key}`);
          });
          authLogger.debug(`[Init] Cleared ${sessionMsalKeys.length} old sessionStorage MSAL key(s)`);
        } else {
          authLogger.debug('[Init] No old sessionStorage MSAL data found');
        }
      } catch (e) {
        logger.warn('INIT', '[Init] Error clearing old sessionStorage', { error: e.message });
      }
    } else {
      authLogger.debug('[Init] Skipping sessionStorage cleanup - active interaction detected');
    }
    
    // CRITICAL FIX: Don't clear cache on every page load!
    // Only clear if there's a stuck interaction (older than 10 minutes)
    // This preserves user sessions across page refreshes and prevents state loss
    authLogger.debug('[Init] Step 2.1: Checking for stuck interactions (preserving cache otherwise)...');
    
    if (!hasActiveInteraction) {
      try {
        const interactionStatus = localStorage.getItem('msal.interaction.status');
        const interactionStart = localStorage.getItem('msal.interaction.start');
        
        if (interactionStatus && interactionStatus !== 'None' && interactionStatus !== 'null') {
          if (interactionStart) {
            const stuckTime = Date.now() - parseInt(interactionStart, 10);
            if (stuckTime > 10 * 60 * 1000) { // 10 minutes (increased from 5)
              logger.warn('INIT', '[Init] Found stuck interaction (older than 10 minutes), clearing cache');
              // PLATFORM-PUBLIC: Skip MSAL cache clearing (no MSAL used)
              // clearMsalCache();
            } else {
              authLogger.debug('[Init] Interaction in progress (not stuck), preserving cache');
            }
          } else {
            authLogger.debug('[Init] No stuck interaction found, preserving cache');
          }
        } else {
          authLogger.debug('[Init] No active interaction, preserving cache (user may be logged in)');
        }
      } catch (e) {
        logger.warn('INIT', '[Init] Error checking interaction status', { error: e.message });
        // Don't clear cache on error - preserve user session
      }
    } else {
      authLogger.debug('[Init] Skipping cache check - active interaction detected, preserving all state');
    }
  }
  
  // =========================================================================
  // STEP 2.5: Clean orphaned interaction states with mismatched authorities
  // This fixes the "No matching interaction state found" loop issue
  // =========================================================================
  
  authLogger.debug('[Init] Step 2.5: Checking for orphaned interaction states with mismatched authorities...');
  
  try {
    // Get the expected authority URL from config
    const expectedBaseAuthority = window._env_?.ENTRA_AUTHORITY_URL;
    
    if (expectedBaseAuthority) {
      // Find all interaction request keys in localStorage
      const allKeys = Object.keys(localStorage);
      const interactionRequestKeys = allKeys.filter(k => 
        k.includes('.request.') || 
        k.includes('interaction.status') ||
        k.includes('.tmp')
      );
      
      let orphanedCount = 0;
      let cleanedCount = 0;
      
      for (const key of interactionRequestKeys) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            // Try to parse and check authority
            try {
              const parsed = JSON.parse(value);
              if (parsed?.authority) {
                // Check if authority matches expected (ignoring trailing slashes and user flow names)
                const storedAuthorityBase = parsed.authority.split('/').slice(0, 4).join('/');
                const expectedAuthorityBase = expectedBaseAuthority.split('/').slice(0, 4).join('/');
                
                if (storedAuthorityBase !== expectedAuthorityBase) {
                  orphanedCount++;
                  logger.warn('INIT', '[Init] Found orphaned state with mismatched authority', {
                    key: key.substring(0, 80) + '...',
                    storedAuthority: parsed.authority,
                    expectedAuthority: expectedBaseAuthority,
                    action: 'will_remove'
                  });
                  
                  // Remove the orphaned state
                  localStorage.removeItem(key);
                  cleanedCount++;
                }
              }
            } catch (parseErr) {
              // Not JSON or doesn't have authority - might be old format
              // Check if it's an old-format interaction key that could cause issues
              if (key.includes('.tmp') && value.length > 500) {
                // Likely an orphaned temp state
                orphanedCount++;
                localStorage.removeItem(key);
                cleanedCount++;
              }
            }
          }
        } catch (keyErr) {
          // Skip this key on error
        }
      }
      
      if (orphanedCount > 0) {
        authLogger.debug(`[Init] Cleaned ${cleanedCount}/${orphanedCount} orphaned interaction state(s)`);
      } else {
        authLogger.debug('[Init] No orphaned interaction states found');
      }
    } else {
      authLogger.debug('[Init] Skipping orphaned state cleanup - no expected authority configured yet');
    }
  } catch (e) {
    logger.warn('INIT', 'Error during orphaned state cleanup', { error: e.message });
  }
  
  // =========================================================================
  // STEP 2.6: Detect and break infinite redirect loops
  // =========================================================================
  
  authLogger.debug('[Init] Step 2.6: Checking for authentication redirect loops...');
  
  try {
    const LOOP_DETECTION_KEY = 'msal.loop.detection';
    const LOOP_THRESHOLD = 3; // Number of redirects in time window to trigger loop detection
    const LOOP_TIME_WINDOW = 30000; // 30 seconds
    
    // Get loop detection state
    let loopState = { timestamps: [], lastReset: 0 };
    try {
      const stored = localStorage.getItem(LOOP_DETECTION_KEY);
      if (stored) {
        loopState = JSON.parse(stored);
      }
    } catch (e) {
      // Ignore parse errors
    }
    
    // Clean old timestamps (older than time window)
    const now = Date.now();
    loopState.timestamps = (loopState.timestamps || []).filter(t => (now - t) < LOOP_TIME_WINDOW);
    
    // If we're on callback page, record this redirect
    if (isAuthCallback) {
      loopState.timestamps.push(now);
      
      // Check if we're in a loop
      if (loopState.timestamps.length >= LOOP_THRESHOLD) {
        logger.error('INIT', '[Init] LOOP DETECTED! Too many auth redirects in short time window', {
          redirectCount: loopState.timestamps.length,
          timeWindow: `${LOOP_TIME_WINDOW / 1000}s`,
          threshold: LOOP_THRESHOLD
        });
        
        // Check if we already tried to reset recently
        if (loopState.lastReset && (now - loopState.lastReset) < 60000) {
          // We tried to reset within the last minute, don't redirect again
          logger.error('INIT', '[Init] Already attempted reset recently, showing error page');
          // Clear loop detection state
          localStorage.removeItem(LOOP_DETECTION_KEY);
        } else {
          // Clear ALL MSAL state and redirect to reset page
          logger.error('INIT', '[Init] Breaking loop by clearing all MSAL state and redirecting to /auth/reset');
          
          // Record that we're doing a reset
          loopState.lastReset = now;
          loopState.timestamps = [];
          localStorage.setItem(LOOP_DETECTION_KEY, JSON.stringify(loopState));
          
          // Clear all MSAL data
          const msalKeys = Object.keys(localStorage).filter(k => 
            k.includes('msal') || k.includes('interaction') || k.includes('.login')
          );
          msalKeys.forEach(key => localStorage.removeItem(key));
          
          // Also clear session storage
          const sessionMsalKeys = Object.keys(sessionStorage).filter(k => 
            k.includes('msal') || k.includes('interaction')
          );
          sessionMsalKeys.forEach(key => sessionStorage.removeItem(key));
          
          // Redirect to reset page with loop reason
          window.location.href = '/auth/reset?reason=loop_detected&source=index';
          return; // Stop initialization
        }
      }
      
      // Save updated loop state
      localStorage.setItem(LOOP_DETECTION_KEY, JSON.stringify(loopState));
    } else {
      // Not on callback page - if no recent redirects, clear the loop state
      if (loopState.timestamps.length === 0) {
        localStorage.removeItem(LOOP_DETECTION_KEY);
      } else {
        // Save cleaned state
        localStorage.setItem(LOOP_DETECTION_KEY, JSON.stringify(loopState));
      }
    }
    
    authLogger.debug('[Init] Loop detection check passed', {
      recentRedirects: loopState.timestamps.length,
      threshold: LOOP_THRESHOLD
    });
  } catch (e) {
    logger.warn('INIT', 'Error during loop detection', { error: e.message });
  }
  
  if (showLoadingScreen) {
    updateLoadingStep(2, 'Verifying security settings...');
  }
  
  // =========================================================================
  // STEP 3: Skip MSAL initialization for platform-public (public site, no auth needed)
  // =========================================================================
  
  // PLATFORM-PUBLIC: This is a public-facing site that does NOT require authentication
  // Users are redirected to platform-auth for sign-in
  // Therefore, we skip ALL MSAL initialization here
  
  // Platform-public: no MSAL instance; MsalProvider is our no-op stub (craco alias).
  if (showLoadingScreen) {
    updateLoadingStep(3, 'Preparing your workspace...');
  }
  
  try {
    // Show final loading step only if loading screen was shown
    if (showLoadingScreen) {
      updateLoadingStep(5, 'Welcome!');
      // Small delay to show completion before rendering app
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Two-frame deferral: first commit = null, second = MsalProvider + shell, third = App.
    // Reduces stack depth per commit to avoid "Maximum call stack size exceeded" (N9/Ay).
    function DeferredApp() {
      const [ready, setReady] = useState(false);
      useEffect(() => {
        const id = requestAnimationFrame(() => {
          if (typeof window !== 'undefined') console.warn('[platform-public] v2 frame 2: mounting App tree');
          setReady(true);
        });
        return () => cancelAnimationFrame(id);
      }, []);
      if (!ready) return null;
      return React.createElement(App);
    }
    function DeferredRoot() {
      const [ready, setReady] = useState(false);
      useEffect(() => {
        const id = requestAnimationFrame(() => {
          if (typeof window !== 'undefined') console.warn('[platform-public] v2 frame 1: mounting MsalProvider shell');
          setReady(true);
        });
        return () => cancelAnimationFrame(id);
      }, []);
      if (!ready) {
        if (typeof window !== 'undefined') console.warn('[platform-public] v2 frame 0: DeferredRoot (null)');
        return null;
      }
      return React.createElement(React.StrictMode, null,
        React.createElement(MsalProvider, null,
          React.createElement(DeferredApp)
        )
      );
    }
    if (typeof window !== 'undefined') console.warn('[platform-public] v2 about to root.render()');
    root.render(React.createElement(DeferredRoot));
    
    // Generate diagnostics report after initial render (allow time for first API calls)
    if (window.__msalDiagnostics) {
      setTimeout(() => {
        try {
          window.__msalDiagnostics.generateReport();
        } catch (reportError) {
          logger.debug('INIT', 'Failed to generate diagnostics report', { error: reportError.message });
        }
      }, 5000); // Wait 5 seconds for first API calls to complete
    }
  } catch (error) {
    console.error('[Init] ❌ Failed to render React app:', error);
    renderErrorUI(root, error);
    return;
  }
  
  // =========================================================================
  // DIAGNOSTICS: Log configuration for debugging (only if DEBUG enabled)
  // =========================================================================
  
  authLogger.debug('[Init] --- DIAGNOSTICS ---');
  authLogger.debug('[Init] User Agent:', navigator.userAgent);
  authLogger.debug('[Init] Current URL:', window.location.href);
  authLogger.debug('[Init] Window env:', window._env_);
  
  // PLATFORM-PUBLIC: Skip MSAL configuration logging (no MSAL used)
  authLogger.debug('[Init] MSAL: Platform-public - authentication not required (public site)');
  
  authLogger.debug('[Init] --- END DIAGNOSTICS ---');
}

// ============================================================================
// ERROR HANDLING & STARTUP
// ============================================================================

/**
 * Global error handler for uncaught errors during initialization
 */
window.addEventListener('error', (event) => {
  console.error('[platform-public] Global uncaught error:', event.message, event.error);
});

/**
 * Global handler for unhandled promise rejections
 */
window.addEventListener('unhandledrejection', (event) => {
  console.error('[platform-public] Unhandled promise rejection:', event.reason);
});

// ============================================================================
// START APPLICATION
// ============================================================================

// Start initialization
initializeApp().catch((error) => {
  logger.error('INIT', '[Init] FATAL: Application initialization failed', { error: error.message });
  
  // Display error to user
  const root = getOrCreateRoot();
  if (root) {
    renderErrorUI(root, error);
  } else {
    // Last resort - direct DOM manipulation
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: #f8d7da; color: #721c24; display: flex; align-items: center; justify-content: center; font-family: Arial, sans-serif; padding: 20px;">
          <div style="max-width: 600px; padding: 40px; background-color: #ffffff; border-radius: 8px;">
            <h1 style="font-size: 28px; font-weight: 600; margin: 0 0 16px 0; color: #721c24;">Application Initialization Error</h1>
            <p style="font-size: 16px; color: #721c24; margin: 0 0 24px 0;">Failed to initialize the application. Please refresh the page.</p>
            <pre style="background-color: #fff3cd; padding: 12px; border-radius: 4px; overflow: auto; font-size: 12px; color: #856404; margin: 0 0 24px 0;">${error.message}</pre>
            <button onclick="window.location.reload()" style="padding: 12px 24px; font-size: 16px; font-weight: 600; color: #ffffff; background-color: #721c24; border: none; border-radius: 6px; cursor: pointer;">Refresh Page</button>
          </div>
        </div>
      `;
    }
  }
});

// ============================================================================
// EXPOSE DIAGNOSTICS TO WINDOW (for debugging in console)
// ============================================================================

// PLATFORM-PUBLIC: Skip MSAL diagnostics (no MSAL used in public site)
if (false && process.env.NODE_ENV === 'development') {
  // MSAL diagnostics disabled for platform-public
}

// ============================================================================
// WAVY BACKGROUND INITIALIZATION
// ============================================================================

let wavyBackgroundCleanup = null;

// Initialize wavy canvas background
function initializeWavyBackground() {
  const canvas = document.getElementById('wavyCanvas');
  if (!canvas) {
    logger.debug('CANVAS', 'Canvas not found, searching for wavyCanvas', { 
      availableCanvases: document.querySelectorAll('canvas').length 
    });
    return;
  }
  logger.debug('CANVAS', 'Canvas found, initializing wavy background');
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  const density = 0.85;
  const curvature = -0.12;
  const numLines = 20;
  let time = 0;
  let lastCssWidth = 0;
  let lastCssHeight = 0;
  let lastDpr = 0;
  let animationFrameId = null;

  const getContainerSize = () => {
    const container = canvas.parentElement;
    const rect = container?.getBoundingClientRect();
    const cssWidth = Math.max(
      1,
      Math.round(rect?.width || container?.clientWidth || window.innerWidth)
    );
    const cssHeight = Math.max(
      1,
      Math.round(rect?.height || container?.clientHeight || window.innerHeight)
    );

    return { cssWidth, cssHeight };
  };

  function resizeCanvas(force = false) {
    const dpr = window.devicePixelRatio || 1;
    const { cssWidth, cssHeight } = getContainerSize();
    const nextWidth = Math.max(1, Math.round(cssWidth * dpr));
    const nextHeight = Math.max(1, Math.round(cssHeight * dpr));

    const hasChanged =
      force ||
      cssWidth !== lastCssWidth ||
      cssHeight !== lastCssHeight ||
      Math.abs(dpr - lastDpr) > 0.001 ||
      canvas.width !== nextWidth ||
      canvas.height !== nextHeight;

    if (!hasChanged) {
      return false;
    }

    canvas.width = nextWidth;
    canvas.height = nextHeight;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.style.maxWidth = '100%';
    canvas.style.maxHeight = '100%';

    // Set transform directly so scale never compounds after repeated resizes.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    lastCssWidth = cssWidth;
    lastCssHeight = cssHeight;
    lastDpr = dpr;

    logger.debug('CANVAS', 'Canvas resized', { cssWidth, cssHeight, dpr, nextWidth, nextHeight });
    return true;
  }

  function drawCurvePattern() {
    resizeCanvas();

    if (lastCssWidth === 0 || lastCssHeight === 0) {
      return;
    }

    // Set canvas background based on theme
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDarkMode) {
      // Dark mode: dark canvas background
      ctx.fillStyle = 'rgba(26, 29, 35, 0.8)';
      ctx.fillRect(0, 0, lastCssWidth, lastCssHeight);
    } else {
      // Light mode: clear canvas (transparent)
      ctx.clearRect(0, 0, lastCssWidth, lastCssHeight);
    }
    
    // Set stroke color
    const strokeColor = isDarkMode ? 'rgba(88, 151, 222, 0.6)' : 'rgba(88, 151, 222, 0.4)';
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;

    for (let i = 0; i <= numLines; i++) {
      const t = i / numLines;
      const startX = t * lastCssWidth;
      const startY = lastCssHeight;
      const waveOffset = Math.sin(time * 0.001 + t * Math.PI * 2) * 20;
      const curveX = lastCssWidth * Math.pow(1 - t, 2) * curvature + waveOffset;
      const curveY = lastCssHeight * (1 - Math.pow(t, 2)) * curvature;
      const endX = curveX;
      const endY = startY - (1 - t) * startY + curveY;

      if (i % Math.round(1 / density) === 0) {
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        const gradient = ctx.createLinearGradient(0, lastCssHeight - 50, 0, lastCssHeight);
        gradient.addColorStop(0, 'rgba(88, 151, 222, 0.3)');
        gradient.addColorStop(1, 'rgba(88, 151, 222, 0.1)');
        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(endX, lastCssHeight);
        ctx.stroke();
      }
    }
  }

  function animate() {
    time += 2; // Much slower movement
    drawCurvePattern();
    animationFrameId = requestAnimationFrame(animate);
  }

  function init() {
    resizeCanvas(true);
    animate();
  }

  const handleResize = () => resizeCanvas(true);
  const handleViewportResize = () => resizeCanvas(true);

  window.addEventListener('resize', handleResize);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportResize);
    window.visualViewport.addEventListener('scroll', handleViewportResize);
  }

  init();

  return () => {
    window.removeEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleViewportResize);
      window.visualViewport.removeEventListener('scroll', handleViewportResize);
    }
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  };
}

function startWavyBackground() {
  if (wavyBackgroundCleanup) {
    wavyBackgroundCleanup();
    wavyBackgroundCleanup = null;
  }

  wavyBackgroundCleanup = initializeWavyBackground();
}

// Initialize canvas after DOM is loaded and React app is rendered
// But skip if dashboard is present (dashboard has its own canvas)
setTimeout(() => {
  // Check if dashboard is present - if so, don't initialize legacy canvas
  const hasDashboard = document.querySelector('.normal-dashboard');
  if (!hasDashboard) {
    logger.debug('CANVAS', 'Initializing wavy background');
    startWavyBackground();
  } else {
    logger.debug('CANVAS', 'Dashboard detected, skipping legacy wavy canvas initialization');
  }
}, 1000); // Increased timeout to ensure React app is fully rendered

// Retry initialization if canvas not found initially
setTimeout(() => {
  // Check if dashboard is present - if so, don't initialize legacy canvas
  const hasDashboard = document.querySelector('.normal-dashboard');
  if (!hasDashboard) {
    const canvas = document.getElementById('wavyCanvas');
    if (!canvas) {
      logger.debug('CANVAS', 'Retrying wavy background initialization');
      startWavyBackground();
    }
  }
}, 2000);

// Re-initialize on theme changes (but skip if dashboard is present)
let lastTheme = document.documentElement.getAttribute('data-theme');
const themeObserver = new MutationObserver(() => {
  const currentTheme = document.documentElement.getAttribute('data-theme');
  if (currentTheme !== lastTheme) {
    lastTheme = currentTheme;
    // Check if dashboard is present - if so, don't reinitialize legacy canvas
    const hasDashboard = document.querySelector('.normal-dashboard');
    if (!hasDashboard) {
      logger.debug('CANVAS', 'Theme changed, re-initializing wavy background');
      setTimeout(() => {
        startWavyBackground();
      }, 100);
    }
  }
});

// Observe theme changes
if (document.documentElement) {
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  });
}
